/**
 * @file        src/lib/navStack.js
 * @description Remembers which path sits at each browser-history index, so an
 *              in-page "Back" button can POP the history (like the browser's own
 *              Back) instead of PUSHING the previous step as a brand-new entry.
 *
 *              Why this exists: every funnel "Back" used to call
 *              navigate('/previous/step'), which is a push. The stack then read
 *              [results, book, review, book] — so pressing the BROWSER's Back on
 *              that last entry walked FORWARD to Review, and the customer was
 *              stuck bouncing between the two pages. React Router exposes no way
 *              to ask "what is the previous entry?", hence this tiny ledger.
 *
 *              React Router's BrowserRouter stamps every entry with
 *              `history.state.idx` (its own stack position). We key the ledger by
 *              that index and mirror it into sessionStorage so it survives a
 *              refresh (sessionStorage is per-tab, exactly like history).
 *
 *              Only index-1 is ever read, and index-1 is always a REAL predecessor
 *              of the current entry, so stale forward entries left behind by a
 *              push are harmless and are not pruned.
 * @author      Kinetika Labs
 * @created     2026-08-03
 * @changelog
 *   - 2026-08-03 Claude — Initial. Backs useStepBack (funnel Back buttons).
 */

const KEY = 'nh:nav-stack';

function load() {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(KEY));
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    // Private mode / disabled storage / corrupt value — start empty. A missing
    // ledger only costs us the pop optimisation; useStepBack falls back safely.
    return [];
  }
}

const stack = load();

function save() {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(stack));
  } catch {
    // Quota or private mode — the in-memory ledger still works for this session.
  }
}

/** Current history stack position per React Router, or null if it isn't ours. */
export function historyIndex() {
  const idx = window.history.state?.idx;
  return Number.isInteger(idx) ? idx : null;
}

/** Record the path now showing at the current history index. */
export function recordVisit(pathname) {
  const idx = historyIndex();
  if (idx === null || stack[idx] === pathname) return;
  stack[idx] = pathname;
  save();
}

/** Path of the entry the browser's own Back button would land on, or null. */
export function previousPath() {
  const idx = historyIndex();
  if (idx === null || idx < 1) return null;
  return stack[idx - 1] || null;
}
